<template>
    <div>
        <!-- PRICING SHELL: the whole pricing block gets its own raised surface -->
        <div class="pricing">
            <div class="ob-dots pricing-dots" />
            <div class="ob-arc pricing-arc-1" />
            <div class="ob-arc pricing-arc-2" />
            <div class="pricing-inner">
                <div class="pricing-head">
                    <div>
                        <div class="ob-signal pricing-signal">
                            <span class="psig-1" />
                            <span class="psig-2" />
                            <span class="psig-3" />
                        </div>
                        <div class="pricing-eyebrow">Home packages</div>
                        <h3 class="pricing-h3">Pick your speed.</h3>
                    </div>
                    <div class="pricing-head-right">
                        <div class="pricing-terms-note">Flexible terms · first router free</div>
                        <div class="pricing-save">Save up to 30% paying in advance</div>
                    </div>
                </div>

                <div class="tier-list">
                    <div v-for="t in rowTiers" :key="t.key" class="tier-row">
                        <div class="tier-grid">
                            <div>
                                <div class="tier-name-wrap"><span class="tier-name">{{ t.name }}</span></div>
                                <div class="tier-who">{{ t.who }}</div>
                            </div>
                            <div class="tier-speed-wrap">
                                <span class="tier-speed">{{ t.speed }}</span>
                                <span class="tier-mbps">Mbps</span>
                            </div>
                            <div>
                                <div class="tier-price">{{ t.price }}<span class="tier-permo">/mo</span></div>
                                <div class="tier-inclusions">
                                    <div class="tier-router">✓ {{ t.router }}</div>
                                    <div class="tier-install">{{ t.install }}</div>
                                </div>
                            </div>
                            <a href="#contact" class="tier-cta ob-press">Get connected</a>
                        </div>
                        <div class="tier-chips">
                            <span v-for="c in t.canDo" :key="c" class="chip"><span class="chip-check">✓</span>{{ c }}</span>
                        </div>
                    </div>
                </div>

                <!-- One Pro: same rhythm as the other tiers, lifted by a light tint and an accent rule -->
                <div class="pro">
                    <div class="pro-rule" />
                    <div class="tier-grid">
                        <div>
                            <div class="tier-name-wrap">
                                <span class="tier-name">{{ pro.name }}</span>
                                <span class="pro-pick">Our pick</span>
                            </div>
                            <div class="tier-who">{{ pro.who }}</div>
                        </div>
                        <div class="tier-speed-wrap">
                            <span class="tier-speed">{{ pro.speed }}</span>
                            <span class="tier-mbps">Mbps</span>
                        </div>
                        <div>
                            <div class="tier-price">{{ pro.price }}<span class="tier-permo">/mo</span></div>
                            <div class="tier-inclusions">
                                <div class="tier-router">✓ {{ pro.router }}</div>
                                <div class="tier-install">{{ pro.install }}</div>
                            </div>
                        </div>
                        <a href="#contact" class="tier-cta tier-cta-filled ob-press">Get connected</a>
                    </div>
                    <div class="tier-chips">
                        <span v-for="c in pro.canDo" :key="c" class="chip chip-pro"><span class="chip-check">✓</span>{{ c }}</span>
                    </div>
                </div>

                <div class="terms-line">
                    <template v-for="(term, i) in HOME_TERMS" :key="term">
                        <span>{{ term }}</span><span v-if="i < HOME_TERMS.length - 1">·</span>
                    </template>
                </div>
            </div>
        </div>

        <!-- household builder -->
        <div class="builder">
            <div>
                <h3 class="builder-h3">Build your household.</h3>
                <div class="builder-tiers">
                    <button
                        v-for="t in allTiers"
                        :key="t.key"
                        class="builder-tier"
                        :class="{ active: store.tier === t.key }"
                        @click="store.tier = t.key"
                    >
                        <span class="builder-tier-label">{{ t.name }}</span>
                        <span class="builder-tier-speed">{{ t.speed }}<span class="builder-tier-mbps"> Mbps</span></span>
                        <span class="builder-tier-price">{{ t.price }}</span>
                    </button>
                </div>
                <div class="builder-addons">
                    <button
                        v-for="a in ADDONS"
                        :key="a.key"
                        class="addon"
                        :class="{ active: store.homeAddons[a.key] }"
                        @click="store.toggleAddon(a.key)"
                    >
                        <span class="addon-left">
                            <span class="addon-box" :class="{ active: store.homeAddons[a.key] }">{{ store.homeAddons[a.key] ? '✓' : '' }}</span>
                            <span>
                                <span class="addon-title">{{ a.title }}</span>
                                <span class="addon-desc">{{ a.desc }}</span>
                            </span>
                        </span>
                        <span class="addon-add">Add</span>
                    </button>
                </div>
                <div class="advance">
                    <div class="advance-k">Pay in advance, pay less</div>
                    <div class="advance-grid">
                        <button
                            v-for="a in ADVANCE"
                            :key="a.key"
                            class="advance-opt"
                            :class="{ active: store.advance === a.key }"
                            @click="store.advance = a.key"
                        >
                            <span class="advance-label">{{ a.label }}</span>
                            <span class="advance-note" :class="{ save: a.pct > 0 }">{{ a.note }}</span>
                        </button>
                    </div>
                </div>
            </div>

            <div class="summary">
                <div class="summary-eyebrow">Your household</div>
                <div class="summary-price-row">
                    <span class="summary-price">{{ store.fmtNLe(store.effectivePrice) }}</span>
                    <span v-if="store.activeAdvance.pct > 0" class="summary-was">{{ store.activeTier.price }}</span>
                </div>
                <div class="summary-line">per month · <span class="summary-tier">{{ store.activeTier.name }}</span> · up to {{ store.activeTier.speed }} Mbps</div>
                <div class="summary-adv">
                    <span class="summary-adv-k">{{ advLabel }}</span><br>{{ advSaving }}
                </div>
                <div class="summary-hr" />
                <div class="summary-rows">
                    <div class="summary-row"><span class="summary-row-k">First router</span><span class="summary-row-v">{{ store.activeTier.router }}</span></div>
                    <div class="summary-row"><span class="summary-row-k">Installation</span><span class="summary-row-v">{{ store.activeTier.install }}</span></div>
                    <div class="summary-row"><span class="summary-row-k">24/7 support</span><span class="summary-row-v">{{ store.activeTier.support }}</span></div>
                    <div class="summary-row"><span class="summary-row-k">Fair-use policy</span><span class="summary-row-v">{{ store.activeTier.fair }}</span></div>
                </div>
                <div class="summary-note">{{ addonNote }}</div>
                <a href="#contact" class="summary-cta ob-press">Get connected →</a>
            </div>
        </div>

        <div class="crosslink">
            <div>
                <div class="crosslink-t">Outgrowing Pro?</div>
                <div class="crosslink-d">If being offline costs you money, Business Connect at 150 Mbps is the next step up — not a different company.</div>
            </div>
            <button class="crosslink-btn ob-press" @click="store.setMode('corp')">Switch to Business →</button>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'

    import { ADDONS, ADVANCE, HOME_TERMS, TIERS } from '@/data/content'
    import { useLandingStore } from '@/stores/landing'

    const store = useLandingStore()

    const rowTiers = [TIERS.lite, TIERS.base]
    const allTiers = [TIERS.lite, TIERS.base, TIERS.pro]
    const pro = TIERS.pro

    const advLabel = computed(() => store.activeAdvance.pct
        ? `${store.activeAdvance.label} up front · ${store.activeAdvance.pct}% off`
        : 'Paid monthly')
    const advSaving = computed(() => store.activeAdvance.pct
        ? `You save ${store.fmtNLe(store.activeTier.priceNum - store.effectivePrice)} a month vs monthly`
        : 'Pay for 3, 6 or 12 months up front and save up to 30%')
    const addonNote = computed(() => store.addonCount
        ? `${store.addonCount} extra${store.addonCount > 1 ? 's' : ''} added — priced with you at survey`
        : 'No extras yet')
</script>

<style scoped>
    .pricing {
        position: relative;
        overflow: hidden;
        border-radius: 34px;
        padding: 44px 46px;
        background: var(--ob-white);
        border: 1px solid var(--ob-navy-a10);
        box-shadow: var(--ob-shadow-feature);
    }
    .pricing-dots { position: absolute; right: -40px; top: -40px; width: 230px; height: 230px; opacity: .5; pointer-events: none; }
    .pricing-arc-1 { position: absolute; left: -120px; bottom: -150px; width: 320px; height: 320px; pointer-events: none; }
    .pricing-arc-2 { position: absolute; left: -70px; bottom: -100px; width: 220px; height: 220px; pointer-events: none; }
    .pricing-inner { position: relative; }
    .pricing-head { display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between; gap: 20px; margin-bottom: 30px; }
    .pricing-signal { display: flex; flex-direction: column; gap: 4px; margin-bottom: 18px; }
    .psig-1 { width: 76px; background: var(--ob-sky); }
    .psig-2 { width: 46px; background: var(--ob-blue); }
    .psig-3 { width: 60px; background: var(--ob-blue-deep); }
    .pricing-eyebrow { font-size: 12px; font-weight: 700; letter-spacing: .16em; text-transform: uppercase; color: var(--ob-blue-deep); }
    .pricing-h3 { margin: 12px 0 0; font-size: clamp(28px, 3.4vw, 42px); font-weight: 800; letter-spacing: -.03em; line-height: 1.02; color: var(--ob-ink); }
    .pricing-head-right { text-align: right; }
    .pricing-terms-note { font-size: 12.5px; color: var(--ob-ink-sub); }
    .pricing-save { margin-top: 6px; font-size: 15px; font-weight: 700; color: var(--ob-blue-deep); }

    .tier-list { border-top: 1px solid var(--ob-navy-a14); }
    .tier-row { padding: 30px 0; border-bottom: 1px solid var(--ob-navy-a09); }
    .tier-grid { display: grid; grid-template-columns: 1.1fr .8fr 1fr auto; gap: 32px; align-items: center; }
    .tier-name-wrap { display: flex; align-items: center; gap: 11px; flex-wrap: wrap; }
    .tier-name { font-size: clamp(22px, 2.4vw, 30px); font-weight: 800; letter-spacing: -.02em; color: var(--ob-ink); }
    .tier-who { margin-top: 8px; font-size: 14.5px; color: var(--ob-ink-sub); }
    .tier-speed-wrap { display: flex; align-items: baseline; gap: 7px; }
    .tier-speed { font-size: clamp(38px, 4.6vw, 60px); font-weight: 800; letter-spacing: -.04em; line-height: .9; color: var(--ob-ink); }
    .tier-mbps { font-size: 14px; font-weight: 600; color: var(--ob-ink-sub); }
    .tier-price { font-size: clamp(20px, 2vw, 26px); font-weight: 800; letter-spacing: -.018em; color: var(--ob-ink); }
    .tier-permo { font-size: 14px; font-weight: 500; color: var(--ob-ink-sub); }
    .tier-inclusions { margin-top: 8px; display: grid; gap: 4px; }
    .tier-router { font-size: 13px; font-weight: 700; color: var(--ob-blue-deep); }
    .tier-install { font-size: 13px; color: var(--ob-ink-sub); }
    .tier-cta {
        padding: 14px 26px;
        border-radius: 999px;
        font-size: 14.5px;
        font-weight: 700;
        white-space: nowrap;
        text-decoration: none;
        background: var(--ob-white);
        color: var(--ob-blue-deep);
        border: 1px solid var(--ob-bluedeep-a35);
    }
    .tier-cta-filled {
        background: var(--ob-blue);
        color: var(--ob-white);
        border: none;
        box-shadow: var(--ob-shadow-glow-sm);
    }
    .tier-chips { margin-top: 18px; padding-top: 16px; border-top: 1px dashed var(--ob-navy-a14); display: flex; flex-wrap: wrap; gap: 8px 10px; align-items: center; }
    .chip {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        font-size: 13px;
        padding: 6px 12px;
        border-radius: 999px;
        border: 1px solid var(--ob-navy-a10);
        background: var(--ob-w-a74);
        color: var(--ob-ink);
    }
    .chip-pro { border-color: var(--ob-bluedeep-a20); background: var(--ob-white); }
    .chip-check { color: var(--ob-blue-deep); font-weight: 700; }

    .pro {
        position: relative;
        border-radius: 20px;
        padding: 28px 30px 26px;
        margin-bottom: 14px;
        background: var(--ob-grad-pro-tint);
        border: 1px solid var(--ob-blue-a22);
    }
    .pro-rule { position: absolute; left: 0; top: 22px; bottom: 22px; width: 3px; border-radius: 3px; background: var(--ob-grad-brand-v); }
    .pro-pick {
        font-size: 10.5px;
        font-weight: 700;
        letter-spacing: .12em;
        text-transform: uppercase;
        padding: 5px 11px;
        border-radius: 999px;
        background: var(--ob-bluedeep-a10);
        color: var(--ob-blue-deep);
    }

    .terms-line { margin-top: 26px; display: flex; flex-wrap: wrap; gap: 10px 26px; font-size: 14px; color: var(--ob-ink-sub); }

    .builder { margin-top: 64px; display: grid; grid-template-columns: 1.5fr 1fr; gap: 26px; align-items: start; }
    .builder-h3 { margin: 0 0 26px; font-size: clamp(24px, 2.8vw, 34px); font-weight: 800; letter-spacing: -.024em; line-height: 1.05; color: var(--ink); }
    .builder-tiers { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
    .builder-tier {
        cursor: pointer;
        text-align: left;
        padding: 22px 24px;
        border-radius: 20px;
        font-family: 'Poppins', sans-serif;
        transition: all .2s;
        border: 1px solid var(--surfBorder);
        background: var(--ob-w-a66);
    }
    .builder-tier.active { border: 2px solid var(--ob-blue-deep); background: var(--ob-w-a90); padding: 21px 23px; }
    .builder-tier-label { display: block; font-size: 13.5px; font-weight: 600; color: var(--ob-ink-mid); }
    .builder-tier-speed { display: block; margin-top: 10px; font-size: 30px; font-weight: 800; letter-spacing: -.03em; line-height: 1; color: var(--ob-ink); }
    .builder-tier-mbps { font-size: 12px; font-weight: 600; color: var(--ob-ink-mid); }
    .builder-tier-price { display: block; margin-top: 8px; font-size: 13.5px; font-weight: 600; color: var(--ob-ink-mid); }

    .builder-addons { margin-top: 20px; display: grid; gap: 11px; }
    .addon {
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 14px;
        padding: 16px 18px;
        border-radius: 14px;
        transition: all .18s;
        border: 1px solid var(--surfBorder);
        background: var(--surf);
        font-family: 'Poppins', sans-serif;
    }
    .addon.active { border: 2px solid var(--ob-blue); background: var(--ob-blue-a10); padding: 15px 17px; }
    .addon-left { display: flex; align-items: center; gap: 14px; text-align: left; }
    .addon-box {
        width: 20px;
        height: 20px;
        border-radius: 6px;
        flex: none;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        color: transparent;
        background: transparent;
        border: 1px solid var(--ob-navy-a24);
    }
    .addon-box.active { background: var(--ob-blue); border: none; color: var(--ob-white); }
    .addon-title { display: block; font-size: 14.5px; font-weight: 600; color: var(--ob-ink); }
    .addon-desc { display: block; margin-top: 3px; font-size: 13px; color: var(--ob-ink-mid); }
    .addon-add { font-size: 12.5px; font-weight: 600; color: var(--ob-blue-deep); white-space: nowrap; }

    .advance { margin-top: 26px; }
    .advance-k { font-size: 13px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: var(--ob-blue-deep); }
    .advance-grid { margin-top: 12px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
    .advance-opt {
        cursor: pointer;
        text-align: center;
        padding: 14px 10px;
        border-radius: 14px;
        font-family: 'Poppins', sans-serif;
        transition: all .2s;
        border: 1px solid var(--surfBorder);
        background: var(--ob-w-a66);
        color: var(--ink);
    }
    .advance-opt.active { border: 2px solid var(--ob-blue-deep); background: var(--ob-w-a90); padding: 13px 9px; }
    .advance-label { display: block; font-size: 14px; font-weight: 700; }
    .advance-note { display: block; margin-top: 4px; font-size: 11.5px; font-weight: 600; color: var(--sub); }
    .advance-note.save { color: var(--ob-blue); }

    .summary {
        position: sticky;
        top: 110px;
        padding: 34px;
        border-radius: 26px;
        color: var(--ob-white);
        background: var(--ob-grad-summary);
        box-shadow: var(--ob-shadow-glow-xl);
    }
    .summary-eyebrow { font-size: 12px; font-weight: 600; letter-spacing: .16em; text-transform: uppercase; color: var(--ob-w-a86); }
    .summary-price-row { margin-top: 14px; display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
    .summary-price { font-size: clamp(34px, 3.6vw, 46px); font-weight: 800; letter-spacing: -.03em; line-height: 1; }
    .summary-was { font-size: 17px; font-weight: 600; color: var(--ob-w-a70); text-decoration: line-through; }
    .summary-line { margin-top: 6px; font-size: 15px; color: var(--ob-w-a92); }
    .summary-tier { font-weight: 700; }
    .summary-adv { margin-top: 14px; padding: 12px 14px; border-radius: 12px; background: var(--ob-black-a20); font-size: 13px; line-height: 1.5; }
    .summary-adv-k { font-weight: 700; }
    .summary-hr { height: 1px; background: var(--ob-w-a32); margin: 24px 0; }
    .summary-rows { display: grid; gap: 11px; font-size: 14px; }
    .summary-row { display: flex; justify-content: space-between; gap: 14px; }
    .summary-row-k { color: var(--ob-w-a84); }
    .summary-row-v { font-weight: 600; }
    .summary-note { margin-top: 22px; padding: 14px 16px; border-radius: 14px; background: var(--ob-black-a20); font-size: 13px; line-height: 1.5; }
    .summary-cta {
        display: block;
        text-align: center;
        margin-top: 20px;
        background: var(--ob-white);
        color: var(--ob-blue);
        font-weight: 700;
        padding: 16px;
        border-radius: 999px;
        text-decoration: none;
    }

    .crosslink {
        margin-top: 44px;
        padding: 26px 30px;
        border: 1px solid var(--surfBorder);
        border-radius: 20px;
        background: var(--surf);
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
    }
    .crosslink-t { font-size: 18px; font-weight: 700; color: var(--ink); }
    .crosslink-d { margin-top: 6px; font-size: 15px; color: var(--sub); max-width: 520px; }
    .crosslink-btn {
        border: none;
        background: var(--ob-blue);
        color: var(--ob-white);
        font-family: 'Poppins', sans-serif;
        font-weight: 700;
        font-size: 14.5px;
        padding: 14px 26px;
        border-radius: 999px;
        cursor: pointer;
        white-space: nowrap;
    }

    @media (max-width: 1024px) {
        .builder { grid-template-columns: 1fr; }
        .summary { position: static; }
    }
    @media (max-width: 900px) {
        .tier-grid {
            grid-template-columns: 1fr 1fr;
            gap: 18px;
        }
        .tier-grid > :nth-child(1) { grid-column: 1 / -1; }
        .tier-grid > :nth-child(4) { grid-column: 1 / -1; text-align: center; }
    }
    @media (max-width: 700px) {
        .pricing { padding: 26px 22px; border-radius: 24px; }
        .pricing-head-right { text-align: left; }
        .pricing-dots { display: none; }
        .builder-tiers { grid-template-columns: 1fr; }
        .advance-grid { grid-template-columns: 1fr 1fr; }
        .summary { padding: 26px 22px; }
    }
</style>
